import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect } from 'react';
import { SESSION_ENDED } from '~/api/ApiProvider';
import { describeError } from '~/api/errors';
import { useSession, useSetupStatus } from '~/api/hooks';
import { isApiError } from '~/api/WorkspaceApi';
import { Button } from '~/ui/Button';
import { ErrorState } from '~/ui/States';
import { DropZone } from './drop';
import { OfflineBanner } from './OfflineBanner';
import { paths } from './paths';

/** Everything behind sign-in: checks the session, then lets screens render. */
export function AppLayout() {
  const session = useSession();
  const setup = useSetupStatus();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const error = session.error;

  useEffect(() => {
    if (setup.data?.needsSetup) {
      void navigate({ to: paths.setup, replace: true });
      return;
    }
    if (isApiError(error, 'not_set_up')) {
      void navigate({ to: paths.signIn, search: { error: 'not_set_up' }, replace: true });
    }
    // 401s are handled once, by the SESSION_ENDED listener below.
  }, [error, setup.data, navigate]);

  // A request anywhere found the session gone (expired, signed out elsewhere, stream ended).
  useEffect(() => {
    const ended = () => {
      const { pathname, search } = window.location;
      if (pathname.startsWith(paths.signIn) || pathname.startsWith(paths.setup)) return;
      qc.removeQueries({ queryKey: ['session'] });
      const back = pathname + search;
      void navigate({
        to: paths.signIn,
        search: back === '/' ? {} : { returnTo: back },
        replace: true,
      });
    };
    window.addEventListener(SESSION_ENDED, ended);
    return () => window.removeEventListener(SESSION_ENDED, ended);
  }, [qc, navigate]);

  if (error && !isApiError(error, 'unauthorized') && !isApiError(error, 'not_set_up')) {
    const d = describeError(error);
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper p-6">
        <div className="w-full max-w-[460px]">
          <ErrorState
            title={d.title}
            action={
              <Button variant="outline" size={40} onClick={() => void session.refetch()}>
                Try again
              </Button>
            }
          >
            {d.body}
          </ErrorState>
        </div>
      </div>
    );
  }

  if (!session.data) {
    return (
      <div
        role="status"
        aria-label="Opening Workspace"
        className="flex min-h-screen items-center justify-center bg-paper"
      >
        <span className="font-display text-22 font-semibold text-ink">Workspace</span>
      </div>
    );
  }
  return (
    <DropZone>
      <OfflineBanner />
      <Suspense
        fallback={<div role="status" aria-label="Opening" className="min-h-screen bg-paper" />}
      >
        <Outlet />
      </Suspense>
    </DropZone>
  );
}
